<template>
  <div class="d-flex m-2" style="flex-direction: column">
    <div v-for="(file, index) in files" :key="index">
      <div class="file-info">
        <div class="file-preview">
          <img v-if="getFileTypeWrapper(file) == 'file'" :src="getImagePath(file)" />
          <img v-else-if="getFileTypeWrapper(file) == 'image'" :src="createURL(file)" />
          <img v-else-if="getFileTypeWrapper(file) == 'video'" :src="getVideoThumbnail()" />
          <img v-else-if="getFileTypeWrapper(file) == 'pdf'" :src="getPDFThumbnail()" />
          <img v-else-if="getFileTypeWrapper(file) == 'office'" :src="getOfficeThumbnail(file)" />

          <!-- ファイルタイプ アイコン -->
          <p v-if="getFileTypeWrapper(file) == 'image'" class="type-icon image">IMAGE</p>
        </div>
        <div class="file-name">{{ getFileName(file) }}</div>
        <delete v-on:deleteFile="onDeleteClick(index)" />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, ref, type PropType } from "vue";
import "./file-list-viewer.scss";
import Delete from "./Delete.vue";
import path from "path";
import S3AccessUtility from "./s3-acceess-utility";

import type { Attachment } from "@/model";
import { AttachmentFactory } from "@/model";
import type { AttachmentFileTypes } from "../suppport-attachment-types";
import { ALLOW_IMAGE_EXTENSIONS, AttachmentFileTypesNone } from "../suppport-attachment-types";
import { OFFICE_TYPE_LIST, judgeOffice, getFileType, getDefaultIcon } from "./file-utility";

// 許可する添付ファイルの拡張子
export const judgeType = (type: string, denyList?: string[]): boolean => {
  if (
    denyList &&
    denyList.some((denyType) =>
      denyType.endsWith("/*") ? type.startsWith(denyType.split("/*")[0]) : denyType == type
    )
  ) {
    // 禁止リストに引っかかった
    return false;
  }
  return (
    type == "image/jpeg" ||
    type == "image/png" ||
    type == "image/gif" ||
    !!type.match(/^video\/.*/g) ||
    type == "application/pdf" ||
    judgeOffice(type)
  );
};

// 許可しないMimeのリストを取得
export const getDenyMimeList = (
  allow_attachment_type: AttachmentFileTypes,
  originDenyList?: string[]
): string[] => {
  const defaultDenyList = originDenyList && originDenyList.length > 0 ? originDenyList : [];

  // 元設定とマージした拡張子を返す
  const result = new Set(["image/*", "video/*", "application/pdf", ...OFFICE_TYPE_LIST]);
  const keys = Object.keys(allow_attachment_type) as (keyof AttachmentFileTypes)[];
  keys.forEach((key) => {
    // 添付許可になっているものは拒否リストから削除
    if (allow_attachment_type[key] == true) {
      switch (key) {
        case "image":
          result.delete("image/*");
          break;
        case "movie":
          result.delete("video/*");
          break;
        case "pdf":
          result.delete("application/pdf");
          break;
        case "office":
          OFFICE_TYPE_LIST.forEach((officeType) => result.delete(officeType));
          break;
      }
    }
  });
  return Array.from(result).concat(...defaultDenyList);
};

// <input>にcaputureが指定されているときのacceptの設定
// MIMEタイプで設定しないとカメラが起動しないためgetAcceptと分けた
export const getCaptureAccept = (allow_attachment_type: AttachmentFileTypes) => {
  return allow_attachment_type !== AttachmentFileTypesNone ? "image/*" : "";
};

export const getVideoAccept = (allow_attachment_type: AttachmentFileTypes) => {
  return allow_attachment_type !== AttachmentFileTypesNone ? "video/*" : "";
};

// acceptの設定
export const getAccept = (
  allow_attachment_type: AttachmentFileTypes,
  originAcceptList?: string[]
): string => {
  const defaultAcceptList =
    originAcceptList && originAcceptList.length > 0
      ? originAcceptList
      : ["image/*", "video/*", "application/pdf", ...OFFICE_TYPE_LIST];

  // 許可リストの作成
  const acceptList = new Set();
  const keys = Object.keys(allow_attachment_type) as (keyof AttachmentFileTypes)[];
  keys.forEach((key) => {
    // 添付許可になっているものは拒否リストから削除
    if (allow_attachment_type[key] == true) {
      switch (key) {
        case "image":
          acceptList.add("image/*");
          break;
        case "movie":
          acceptList.add("video/*");
          break;
        case "pdf":
          acceptList.add("application/pdf");
          break;
        case "office":
          OFFICE_TYPE_LIST.forEach((officeType) => acceptList.add(officeType));
          break;
      }
    }
  });

  // ↑で作ったリストをフィルタリング
  const results = Array.from(acceptList).filter((accept) =>
    defaultAcceptList.some((defaultAccept) => defaultAccept == accept)
  );
  let accept = results.join(", ");
  if (accept.match("image/*")) {
    accept = accept.replace("image/*", ALLOW_IMAGE_EXTENSIONS.join(", "));
  }
  return accept;
};

export default defineComponent({
  name: "file-list-viewer",
  components: { Delete },
  props: {
    files: { type: Array as PropType<(File | Attachment)[]>, default: () => [] },
    param: {
      type: Object as PropType<{
        topicId?: string;
        messageId: string;
        commentId: string;
      }>,
      default: () => ({ messageId: "", commentId: "" }),
    },
    thumbStore: { type: Array as PropType<File[]>, default: () => [] },
  },
  setup(props, { emit }) {
    const imageStore = ref<{ name: string; path: string }[]>([]);

    // created 相当（async・fire-and-forget）
    (async (): Promise<void> => {
      const s3AccessUtility = S3AccessUtility.getInstance();
      await Promise.all(
        props.files.map(async (file) => {
          if (AttachmentFactory.isAttachment(file)) {
            const name = file.url;
            const thumbPath = (await s3AccessUtility.getThumbFile(
              name,
              props.param,
              "image/jpg"
            )) as string;
            imageStore.value.push({ name: name, path: thumbPath });
          }
        })
      );
    })();

    // 動画のサムネイルアイコン取得
    const getVideoThumbnail = (): string => {
      const VIDEO_THUMBNAIL_ICON = "/img/icon/icon_file_movie.svg";
      return VIDEO_THUMBNAIL_ICON;
    };

    // PDFのサムネイルアイコン取得
    const getPDFThumbnail = (): string => {
      const PDF_THUMBNAIL_ICON = "/img/icon/icon_file_pdf.svg";
      return PDF_THUMBNAIL_ICON;
    };

    // officeのサムネアイコン取得
    const getOfficeThumbnail = (file: File | Attachment): string => {
      return getDefaultIcon(file);
    };

    const onLoadThumbnail = (e: { target: HTMLImageElement }): void => {
      if (e.target.getAttribute("src") !== "/img/icon/icon_file_pdf.svg") {
        // サムネイル表示の場合、PDFラベルを表示
        (e.target.nextElementSibling! as HTMLParagraphElement).style.display = "inline";
      } else {
        // PDFラベルを非表示
        (e.target.nextElementSibling! as HTMLParagraphElement).style.display = "none";
      }
    };

    const getFileName = (file: File | Attachment): string => {
      if (AttachmentFactory.isAttachment(file)) {
        let fileStr = file.url;
        if (fileStr.slice(-1) == "/") {
          fileStr = fileStr.slice(0, -1);
        }
        const splitUrl = fileStr.split(path.sep);
        return S3AccessUtility.decodeName(splitUrl[splitUrl.length - 1]);
      } else {
        return file.name;
      }
    };

    // getFileTypeをラッパー *vueでのエラー防止
    const getFileTypeWrapper = (file: File | Attachment) => {
      return getFileType(file);
    };

    const getImagePath = (file: File | Attachment | string): string => {
      const name = AttachmentFactory.isAttachment(file) ? file.url : file;
      const image = imageStore.value.find((obj) => obj.name == name);
      return image ? image.path : "";
    };

    const getThumbnail = (file: File): File | undefined => {
      const name = `${file.name + file.size}_tmb.jpg`;
      const pdfThumb = props.thumbStore.find((thumb) => thumb.name === name);
      return pdfThumb;
    };

    const createURL = (file: File | Attachment | string): string => {
      let localPath;
      if (AttachmentFactory.isAttachment(file)) {
        return getImagePath(file);
      } else if (typeof file == "string") {
        return getImagePath(file);
      } else if (getFileType(file) == "pdf" || getFileType(file) == "video") {
        const thumb = getThumbnail(file);
        if (!thumb) {
          if (getFileType(file) == "pdf") {
            return "/img/icon/icon_file_pdf.svg";
          } else {
            return "/img/icon/icon_file_movie.svg";
          }
        }
        localPath = thumb;
      } else {
        localPath = file;
      }
      return URL.createObjectURL(localPath);
    };

    const onDeleteClick = (index: number): void => {
      emit("deleteFile", index);
    };

    return {
      imageStore,
      getVideoThumbnail,
      getPDFThumbnail,
      getOfficeThumbnail,
      onLoadThumbnail,
      getFileName,
      getFileTypeWrapper,
      getImagePath,
      getThumbnail,
      createURL,
      onDeleteClick,
    };
  },
});
</script>
